import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Grid,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  Language,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import {
  fetchDashboardAnalytics,
  type DashboardAnalyticsResponse,
  type DashboardAnalyticsAnomaly,
} from '../../services/analytics.service';
import { metricsService } from '../../services/metricsService';
import { useSiteStore, type Site } from '../../store/site.store';
import { useAuthStore } from '../../store/auth.store';
import { isAgencyScopedProfile, shouldShowAgencyBackLink } from '../../utils/analyticsNavigation';
import {
  getCustomerHealthChipColor,
  getLatestCustomerRefreshTimestamp,
} from './customerDashboardData';

// Colors
const COLORS = {
  critical: '#d32f2f',
  high: '#f57c00',
  medium: '#fbc02d',
  low: '#4caf50',
  success: '#2e7d32',
  info: '#0288d1',
};

// Page option type
interface PageOption {
  id: number;
  url: string;
  label: string;
}

type HealthColor = 'default' | 'success' | 'warning' | 'error';

interface HealthStatus {
  label: string;
  color: HealthColor;
  icon: JSX.Element;
}

// Data state enum
enum DataState {
  NO_DATA = 'no_data',
  LOADING = 'loading',
  AVAILABLE = 'available',
  ERROR = 'error'
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { siteId: routeSiteId } = useParams<{ siteId?: string }>();
  const [loading, setLoading] = useState(true);
  const [dataState, setDataState] = useState<DataState>(DataState.LOADING);
  const [error, setError] = useState<string | null>(null);
  
  // Use global site store with proper selectors for reactivity
  const selectedSite = useSiteStore(state => state.selectedSite);
  const sites = useSiteStore(state => state.sites);
  const setSites = useSiteStore(state => state.setSites);
  const setSite = useSiteStore(state => state.setSite);
  const user = useAuthStore(state => state.user);
  
  console.log('🔍 [RENDER] CustomerDashboard rendered with selectedSite:', selectedSite?.id, selectedSite?.name);
  
  // Monitor site changes
  useEffect(() => {
    console.log('⚡ [USEEFFECT] selectedSite changed:', {
      id: selectedSite?.id,
      name: selectedSite?.name,
      pagesQueryKey: ['pages-for-site', selectedSite?.id]
    });
  }, [selectedSite?.id, selectedSite?.name]);
  
  // Page selector state
  const [selectedPage, setSelectedPage] = useState<PageOption | null>(null);
  
  // Data states
  const [dashboardData, setDashboardData] = useState<DashboardAnalyticsResponse | null>(null);
  const [topAnomalies, setTopAnomalies] = useState<DashboardAnalyticsAnomaly[]>([]);
  const [selectedSiteDetail, setSelectedSiteDetail] = useState<DashboardAnalyticsResponse['sites'][number] | null>(null);

  // Use the persisted auth tenant as the dashboard scope source.
  // Site selection is still used for page context, but not for the dashboard endpoint scope.
  const siteId = selectedSite?.id;
  // An agency user's own tenant is not a customer scope, so the selected tenant is required.
  const effectiveTenantId = isAgencyScopedProfile(user)
    ? user?.proxyTenantId
    : (user?.proxyTenantId || user?.tenantId);
  const dashboardScopeId = effectiveTenantId || selectedSite?.organizationId || siteId;
  const hasDashboardScope = dashboardScopeId !== undefined && dashboardScopeId !== null && String(dashboardScopeId).trim() !== '';
  const showAgencyBackLink = shouldShowAgencyBackLink(routeSiteId, location.state);

  // Load sites on mount
  const loadSites = useCallback(async () => {
    try {
      console.log('Loading sites...');
      const sitesData = await metricsService.getSites();
      console.log('Sites loaded:', sitesData);
      setSites(sitesData); // Update global store
      if (sitesData && sitesData.length > 0 && !selectedSite) {
        // Set first site as default for non-customer users
        setSite(sitesData[0], queryClient);
        console.log('First site selected:', sitesData[0].id);
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  }, [queryClient, selectedSite, setSite, setSites]);

  const loadData = useCallback(async () => {
    if (!hasDashboardScope) {
      console.log('No dashboard scope available, skipping data load');
      setDataState(DataState.NO_DATA);
      setLoading(false);
      return;
    }

    setLoading(true);
    setDataState(DataState.LOADING);
    setError(null);
    try {
      const dashboardScope = 'customer' as const;
      const resolvedDashboardScopeId = String(dashboardScopeId);

      console.log('🔍 [DASHBOARD] Fetching analytics data', {
        scope: dashboardScope,
        scopeId: resolvedDashboardScopeId,
        routeSiteId,
        siteId,
        effectiveTenantId,
      });

      const dashboardRes = await fetchDashboardAnalytics(dashboardScope, resolvedDashboardScopeId, 10);
      const dashboardData = dashboardRes.data;
      setDashboardData(dashboardData);
      const anomalies = dashboardData.anomalies || [];
      const opportunities = dashboardData.opportunities || [];
      const summary = dashboardData.summary || {};

      const reportSites = (dashboardData.sites || []).map((site) => ({
        id: site.id,
        name: site.name || `Site #${site.id}`,
        url: site.url || '',
        organizationId: Number(site.tenant_id || dashboardScopeId || 0),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as Site[];

      if (reportSites.length > 0) {
        setSites(reportSites);
        if (!selectedSite && !routeSiteId) {
          const fallbackSite = reportSites[0];
          setSite(fallbackSite as Site, queryClient);
        }
      }

      console.log('📊 [DASHBOARD] API Responses:', {
        scope: dashboardScope,
        summary,
        anomaliesCount: anomalies.length,
        opportunitiesCount: opportunities.length,
        reportSitesCount: reportSites.length,
      });

      setTopAnomalies(anomalies as DashboardAnalyticsAnomaly[]);

      if ((summary.total_anomalies || 0) === 0 && anomalies.length === 0) {
        console.log('⚠️ [DASHBOARD] No data found, setting NO_DATA state');
        setDataState(DataState.NO_DATA);
      } else {
        console.log('✅ [DASHBOARD] Data loaded, setting AVAILABLE state');
        setDataState(DataState.AVAILABLE);
      }
    } catch (error: unknown) {
      console.error('Dashboard data error:', error);
      setDataState(DataState.ERROR);
      const responseStatus = typeof error === 'object' && error !== null && 'response' in error
        ? (error.response as { status?: number } | undefined)?.status
        : undefined;
      // User-friendly error messages
      if (responseStatus === 422) {
        setError('We couldn\'t process analytics data right now. This usually happens when required connections or permissions are missing.');
      } else if (responseStatus === 404) {
        setError('No analytics data found. Please ensure your site is connected to Google Search Console.');
      } else {
        setError('Unable to load dashboard data. Please check your data connections.');
      }
    } finally {
      setLoading(false);
    }
  }, [dashboardScopeId, effectiveTenantId, hasDashboardScope, queryClient, routeSiteId, selectedSite, setSite, setSites, siteId]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    if (routeSiteId && sites.length > 0) {
      const parsedRouteSiteId = Number(routeSiteId);
      const matchedSite = sites.find((site) => site.id === parsedRouteSiteId);
      if (matchedSite && (!selectedSite || selectedSite.id !== matchedSite.id)) {
        setSite(matchedSite, queryClient);
      }
    } else if (!selectedSite && sites.length > 0) {
      const fallbackSite = sites[0];
      setSite(fallbackSite, queryClient);
    }
  }, [queryClient, routeSiteId, selectedSite, setSite, sites]);

  // Load data when site changes
  useEffect(() => {
    if (siteId) {
      void loadData();
    }
  }, [loadData, siteId]);

  // Reload when page selection changes
  const handlePageChange = () => {
    setSelectedPage(null);
    void loadData();
  };

  const summary = dashboardData?.summary;
  const dashboardSites = dashboardData?.sites ?? [];
  const healthDistribution = dashboardData?.health_distribution;
  const healthScore = dataState === DataState.AVAILABLE
    ? summary?.average_score ?? null
    : null;

  const getHealthStatus = (): HealthStatus => {
    if (healthScore === null) return { label: 'Data Unavailable', color: 'default', icon: <Warning /> };
    if (summary?.health_bucket === 'critical') return { label: 'Critical', color: 'error', icon: <ErrorIcon /> };
    if (summary?.health_bucket === 'needs_attention') return { label: 'Needs Attention', color: 'warning', icon: <Warning /> };
    return { label: 'Healthy', color: 'success', icon: <CheckCircle /> };
  };

  const healthStatus = getHealthStatus();

  const headerSummary = {
    customerName: summary?.customer_name || selectedSite?.name || 'Customer Dashboard',
    websiteCount: summary?.website_count || dashboardSites.length || 0,
    status: healthStatus.label,
    accountManager: 'Assigned team',
    reportingPeriod: 'Last 30 days',
  };

  const getSiteHealthLabel = (bucket?: string) => {
    const normalized = String(bucket ?? '').trim().toLowerCase();
    if (normalized === 'critical') return 'Critical';
    if (normalized === 'needs_attention' || normalized === 'needs attention' || normalized === 'issues') return 'Needs Attention';
    return 'Healthy';
  };

  const healthBreakdown = [
    { label: 'Healthy', value: healthDistribution?.healthy ?? summary?.healthy_site_count ?? 0 },
    { label: 'Good', value: healthDistribution?.good ?? 0 },
    { label: 'Needs Attention', value: healthDistribution?.issues ?? summary?.needs_attention_site_count ?? 0 },
    { label: 'Critical', value: healthDistribution?.critical ?? summary?.critical_site_count ?? 0 },
  ];

  const clicks = summary?.clicks ?? 0;
  const impressions = summary?.impressions ?? 0;
  const ctr = impressions > 0 ? `${((clicks / impressions) * 100).toFixed(1)}%` : '0%';
  const latestRefreshTimestamp = getLatestCustomerRefreshTimestamp(dashboardSites);
  const lastRefresh = latestRefreshTimestamp
    ? new Date(latestRefreshTimestamp).toLocaleString()
    : 'Not available';

  const kpiCards = [
    { label: 'Health score', value: healthScore ?? 0, helperText: 'Portfolio quality score' },
    { label: 'Critical issues', value: summary?.critical_issue_count ?? summary?.critical_count ?? 0, helperText: 'Needs immediate action' },
    { label: 'Open issues', value: summary?.open_issue_count ?? summary?.total_anomalies ?? 0, helperText: 'Active anomalies' },
    { label: 'Search clicks', value: clicks, helperText: 'Search Console clicks' },
    { label: 'Impressions', value: impressions, helperText: 'Search visibility' },
    { label: 'CTR', value: ctr, helperText: 'Click-through rate' },
  ];

  const websiteSummaryRows = dashboardSites.map((site) => ({
    id: site.id,
    website: site.site_name || site.name || site.url || `Site #${site.id}`,
    seoScore: site.score ?? 0,
    health: getSiteHealthLabel(site.health_bucket),
    criticalIssues: site.critical_issues || 0,
    openIssues: site.open_issues || 0,
    actions: 'View',
  }));

  const trendData = (dashboardData?.search_performance || []).map((point) => ({
    name: point.date || '',
    clicks: point.clicks ?? 0,
    impressions: point.impressions ?? 0,
  }));

  const seoScoreTrendData = (dashboardData?.seo_score_trend || []).map((point) => ({
    name: point.date || '',
    score: Number(point.score ?? 0),
  }));

  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const issueRows = (dashboardData?.anomalies || [])
    .map((issue) => ({
      issue: issue.title || issue.metric,
      severity: issue.severity || 'medium',
      status: issue.status || 'active',
      page: issue.page,
    }))
    .sort((left, right) => (severityOrder[left.severity] ?? 4) - (severityOrder[right.severity] ?? 4));

  const siteHealthPieData = healthBreakdown.map((item) => ({
    name: item.label,
    value: item.value,
    color: item.label === 'Critical'
      ? COLORS.critical
      : item.label === 'Issues'
        ? COLORS.high
        : item.label === 'Good'
          ? COLORS.info
          : COLORS.success,
  })).filter((item) => item.value > 0);

  const emptyStateMessage = 'No dashboard analytics data available for the selected scope.';

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">Loading your dashboard...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Dialog open={Boolean(selectedSiteDetail)} onClose={() => setSelectedSiteDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span">{selectedSiteDetail?.site_name || selectedSiteDetail?.name || 'Site details'}</Typography>
            <IconButton edge="end" aria-label="close" onClick={() => setSelectedSiteDetail(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSiteDetail ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">Site URL</Typography>
                <Typography variant="body1">{selectedSiteDetail.site_url || selectedSiteDetail.url || '—'}</Typography>
              </Box>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Score</Typography>
                  <Typography variant="h6">{Number(selectedSiteDetail.score ?? 0).toFixed(1)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Health</Typography>
                  <Typography variant="h6">{getSiteHealthLabel(selectedSiteDetail.health_bucket)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Open issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.open_issues ?? selectedSiteDetail.critical_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Critical issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.critical_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">High issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.high_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Medium issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.medium_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Low issues</Typography>
                  <Typography variant="body1">{selectedSiteDetail.low_issues ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Clicks</Typography>
                  <Typography variant="body1">{selectedSiteDetail.clicks ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Impressions</Typography>
                  <Typography variant="body1">{selectedSiteDetail.impressions ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Traffic</Typography>
                  <Typography variant="body1">{selectedSiteDetail.traffic ?? 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Trend</Typography>
                  <Typography variant="body1">{selectedSiteDetail.trend || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Agency ID</Typography>
                  <Typography variant="body1">{selectedSiteDetail.agency_id || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">Tenant ID</Typography>
                  <Typography variant="body1">{selectedSiteDetail.tenant_id || '—'}</Typography>
                </Grid>
              </Grid>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {selectedPage
              ? `Page ${selectedPage.id}`
              : (selectedSite?.name || headerSummary.customerName || 'Customer Dashboard')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedPage ? 'Page-level customer view' : `${headerSummary.websiteCount} website${headerSummary.websiteCount === 1 ? '' : 's'} • ${headerSummary.status}`}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          {showAgencyBackLink ? (
            <Button size="small" variant="outlined" onClick={() => navigate('/analytics/agency')}>
              ← Agency overview
            </Button>
          ) : null}
          <Button size="small" variant="outlined" onClick={() => handlePageChange()}>
            Run Audit
          </Button>
          <Button size="small" variant="contained">
            Generate Report
          </Button>
          {selectedSite ? (
            <Chip icon={<Language />} label={selectedSite.name || selectedSite.url} color="primary" variant="outlined" />
          ) : null}
        </Stack>
      </Stack>

      {/* Error State with Retry */}
      {dataState === DataState.ERROR && error && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Chip 
                label="Retry" 
                size="small" 
                icon={<Refresh />}
                onClick={() => loadData()}
                sx={{ cursor: 'pointer' }}
              />
            </Stack>
          }
        >
          <Typography variant="subtitle2" fontWeight="bold">⚠️ Data not available yet</Typography>
          <Typography variant="body2">{error}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            This does not indicate a problem with your website.
          </Typography>
        </Alert>
      )}

      {/* Main summary surface */}
      {dataState === DataState.ERROR ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold">Analytics data unavailable</Typography>
          <Typography variant="body2">{error || emptyStateMessage}</Typography>
        </Alert>
      ) : dataState === DataState.NO_DATA ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold">No customer data available</Typography>
          <Typography variant="body2">{emptyStateMessage}</Typography>
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Typography variant="h6">{selectedSite?.name || headerSummary.customerName || 'Customer Overview'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {headerSummary.websiteCount} website{headerSummary.websiteCount === 1 ? '' : 's'} • {headerSummary.accountManager} • {headerSummary.reportingPeriod}
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Customer', value: headerSummary.customerName },
                    { label: 'Websites', value: headerSummary.websiteCount },
                    { label: 'Account manager', value: headerSummary.accountManager },
                    { label: 'Last refresh', value: lastRefresh },
                    { label: 'Reporting period', value: headerSummary.reportingPeriod },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} key={item.label}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>{item.value}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>Customer Site Health</Typography>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={siteHealthPieData.length > 0 ? siteHealthPieData : [{ name: 'No data', value: 100, color: '#e0e0e0' }]}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {(siteHealthPieData.length > 0 ? siteHealthPieData : [{ name: 'No data', value: 100, color: '#e0e0e0' }]).map((entry, index) => (
                          <Cell key={`${entry.name}-${index}`} fill={entry.color || '#90caf9'} />
                        ))}
                      </Pie>
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Stack spacing={1.25}>
                  {healthBreakdown.map((item) => (
                    <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{item.label}</Typography>
                      <Chip size="small" label={item.value} color={item.label === 'Critical' ? 'error' : item.label === 'Issues' ? 'warning' : item.label === 'Good' ? 'info' : 'success'} />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>{card.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{card.helperText}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Websites</Typography>
                <Button size="small" variant="outlined" onClick={() => navigate('/analytics/pages')}>
                  Manage websites
                </Button>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Website</TableCell>
                      <TableCell>SEO score</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell>Critical issues</TableCell>
                      <TableCell>Open issues</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {websiteSummaryRows.map((row) => (
                      <TableRow
                        key={row.website}
                        hover
                        onClick={() => setSelectedSiteDetail(dashboardSites.find((site) => Number(site.id) === Number(row.id)) ?? null)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{row.website}</TableCell>
                        <TableCell>{row.seoScore}</TableCell>
                        <TableCell><Chip size="small" label={row.health} color={getCustomerHealthChipColor(row.health)} /></TableCell>
                        <TableCell>{row.criticalIssues}</TableCell>
                        <TableCell>{row.openIssues}</TableCell>
                        <TableCell>{row.actions}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Highest-priority open issues</Typography>
              <Stack spacing={1.5}>
                {issueRows.length > 0 ? issueRows.slice(0, 4).map((row, idx) => (
                  <Paper key={`${row.issue}-${idx}`} variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="body2" fontWeight="medium">{row.issue}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.severity} • {row.status}</Typography>
                    {row.page ? <Typography variant="caption" color="text.secondary" display="block">{row.page}</Typography> : null}
                  </Paper>
                )) : <Alert severity="info">No issues detected.</Alert>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Google Search Performance Trend</Typography>
              <Box sx={{ height: 240 }}>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <ChartTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="clicks" stroke="#1976d2" strokeWidth={2} name="Clicks" />
                      <Line type="monotone" dataKey="impressions" stroke="#2e7d32" strokeWidth={2} name="Impressions" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Alert severity="info">No dated Google Search Console data is available.</Alert>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>SEO Score Trend</Typography>
              <Typography variant="body2" color="text.secondary">SEO score movement over time</Typography>
              <Box sx={{ height: 220 }}>
                {seoScoreTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={seoScoreTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} />
                      <ChartTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="score" stroke="#ff9800" strokeWidth={2} name="SEO Score" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Alert severity="info">No SEO score history is available.</Alert>}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Search Performance</Typography>
              <Typography variant="body2" color="text.secondary">Clicks and impressions for the current period</Typography>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'Clicks', value: clicks },
                    { name: 'Impressions', value: impressions },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip />
                    <Bar dataKey="value" fill="#7b1fa2" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {topAnomalies.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Recent Signals</Typography>
              <Button size="small" variant="outlined" onClick={() => navigate('/actions-next')}>
                View Action Center
              </Button>
            </Stack>
            <Stack spacing={1}>
              {topAnomalies.slice(0, 5).map((anomaly) => (
                <Paper key={anomaly.id} variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderLeft: 4, borderLeftColor: anomaly.severity === 'critical' ? 'error.main' : anomaly.severity === 'high' ? 'warning.main' : anomaly.severity === 'medium' ? 'info.main' : 'success.main' }} onClick={() => navigate(`/actions-next/${anomaly.id}`)}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box>
                        <Typography variant="body1" fontWeight="medium">{anomaly.title || anomaly.metric}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[anomaly.description, anomaly.page, anomaly.created_at ? new Date(anomaly.created_at).toLocaleDateString() : null].filter(Boolean).join(' • ')}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Chip label={anomaly.status} size="small" variant="outlined" />
                      <Chip label={anomaly.severity} size="small" color={anomaly.severity === 'critical' ? 'error' : anomaly.severity === 'high' ? 'warning' : 'default'} />
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
